<script setup lang="ts">
import { Ref, ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import {
  Contexts,
  NamedBindings,
  TranslationService,
  UserServiceInterface,
  getContainer,
} from '@dandelion/core';
import '/node_modules/flag-icons/css/flag-icons.min.css';
import { userPreferencesWithPossibleValues } from '../../entities/PreferenceValueConfigs';
import PreferencesMultiSelectorPanel from './PreferencesMultiSelectorPanel.vue';
import { Preferences } from '../../enum/Preferences';
import { PreferencesService } from '../../services/PreferencesService';
import { NamedServices } from '../../enum/NamedServices';
import { Preference, PreferenceValueConfig } from '../../entities/Preference';
import { Users } from '../../enum/Users';

const router = useRouter();
const sharedContainer = getContainer(Contexts.SHARED);
const ts = sharedContainer.get(
  NamedBindings.TranslationService
) as TranslationService;
const userService: UserServiceInterface = sharedContainer.get(NamedBindings.UserService);
const userPreferencesService = sharedContainer.get(
  NamedServices.UserPreferencesService
) as PreferencesService;
const userPreferencesDialogRef = ref<HTMLDialogElement | null>(null);

const preferencesValues = userPreferencesService.getPreferences().reduce(
  (result, p) => {
    // We detach reactivity here: we want an internal copy, that eventually will be synchronized
    // with the service on the "confirm" click
    result[p.preferenceName] = ref(toValue(p.value));
    return result;
  }, {} as Record<Preferences, Ref<string>>
);

const preferencesConfig = userPreferencesWithPossibleValues.reduce(
  (result, p) => {
    result[p.name] = p.possibleValues.reduce(
      (r, v) => {
        r[v.value] = v;
        return r;
      }, {} as Record<string, PreferenceValueConfig>
    );
    return result;
  }, {} as Record<Preferences, Record<string, PreferenceValueConfig>>
);

const currentPanel: Ref<Preferences> = ref(Preferences.USERPREFERENCES);

function toggleValue(preferenceName: Preferences) {
  const currentValue = preferencesValues[preferenceName];
  const possibleValues = Object.keys(preferencesConfig[preferenceName]);
  if (possibleValues.length != 2) {
    console.error(`
      There was an error when toggling preferences: the preference ${preferenceName}
      has ${possibleValues.length} option, but 'toggleValue',
      which supports only two options, has been called on it!
    `);
  }
  return currentValue.value === possibleValues[0] ? possibleValues[1] : possibleValues[0];
}

function showPanel() {
  if (userPreferencesDialogRef?.value) {
    userPreferencesDialogRef.value.showModal();
  }
}

function close() {
  currentPanel.value = Preferences.USERPREFERENCES
  userPreferencesDialogRef.value?.close();
}

async function confirm() {
  for (const [ preference, value ] of Object.entries(preferencesValues)) {
    await userPreferencesService.setPreference(preference as Preferences, value.value);
  }

  router.go(0);
}

function onPreferenceClick(preference: Preference) {
  if (preference.possibleValues?.length > 2) {
    currentPanel.value = preference.name;
  } else if (preference.name == Preferences.USERCONTROLPANEL) {
    if ([ Users.SUPPORT_ADMIN, Users.ADMIN ].includes(userService.getCurrentUser().value?.role)) {
        window.location.href = `${import.meta.env.VITE_APP_AUTH_BASE_URL}UserManager`;
    }
  } else {
    preferencesValues[preference.name].value = toggleValue(preference.name);
  }
}

function onValueSelected(preferenceName: Preferences, newValue: string) {
  preferencesValues[preferenceName].value = newValue;
  currentPanel.value = Preferences.USERPREFERENCES;
}

</script>

<template>
  <dialog ref="userPreferencesDialogRef" class="preferences-dialog">
    <template v-if="currentPanel == Preferences.USERPREFERENCES">
      <header>
        <h2 v-html="ts.t('User preferences')"></h2>
        <ion-icon name="close-outline" @click="close()"></ion-icon>
      </header>
      <main>
        <div
          v-for="preference in userPreferencesWithPossibleValues"
          class="preferences-dialog-card"
        >
          <div  v-if="!(preference.name === 'userControlPanel' && !(userService.getCurrentUser().value?.role === Users.SUPPORT_ADMIN ||
                    userService.getCurrentUser().value?.role === Users.ADMIN))"
            :class="{
              'preferences-dialog-icon-wrapper': true,
              [preference.name.toLowerCase()]: true,
              'icon-active': [ 'true', 'dark' ].includes(preferencesValues[preference.name].value)
            }"
            @click="onPreferenceClick(preference)"
          >
            <ion-icon
              :name="preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.icon"
            ></ion-icon>
            <span
              :class="preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.icon"
            ></span>
          </div>
          <p
            class="preferences-dialog-description"
            v-html="
              ts.t(preference.label) +
              preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.description
            "
          ></p>
          <span
            v-if="preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.icon"
            :class="preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.icon"
          ></span>
        </div>
      </main>
      <footer>
        <button
          type="submit"
          @click="confirm()"
          v-html="ts.t('Confirm')"
        ></button>
      </footer>
    </template>

    <PreferencesMultiSelectorPanel
      v-if="currentPanel == Preferences.LANGUAGES"
      title="Language"
      :options="preferencesConfig[Preferences.LANGUAGES]"
      :selectedOption="preferencesValues[Preferences.LANGUAGES]"
      @close="close()"
      @back-to-preferences="currentPanel = Preferences.USERPREFERENCES"
      @value-selected="(value: string) => onValueSelected(Preferences.LANGUAGES, value)"
    />
  </dialog>

  <button
    id="user-preferences-dialog-btn" class="secondary-button"
    @click="showPanel()"
  >
    <ion-icon name="person-outline"></ion-icon>
    <ion-icon name="settings-outline"></ion-icon>
  </button>
</template>
